import React, { useMemo, useState } from 'react';
import Avatar from '../components/Avatar';
import Icon from '../components/Icon';
import { Alert, BotBadge, EmptyState, LoadingBlock, TopBar } from '../components/ui';
import { timeAgo } from '../lib/format';

export default function MessagesScreen({ me, conversations, loading, error, onRefresh, onOpenChat, onGoDiscover }) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return conversations;
    return conversations.filter(
      (conv) =>
        conv.user?.username?.toLowerCase().includes(needle) ||
        conv.lastMessage?.content?.toLowerCase().includes(needle)
    );
  }, [conversations, query]);

  return (
    <div className="screen">
      <TopBar
        title="Messages"
        subtitle={conversations.length > 0 ? `${conversations.length} conversation${conversations.length === 1 ? '' : 's'}` : undefined}
        right={
          <button
            type="button"
            className="icon-btn icon-btn-plain"
            onClick={onRefresh}
            aria-label="Refresh conversations"
            disabled={loading}
          >
            <Icon name="refresh" size={18} />
          </button>
        }
      />

      <div className="screen-body">
        {conversations.length > 0 && (
          <div className="field-input-wrap" style={{ margin: '16px 0 8px' }}>
            <span className="field-icon">
              <Icon name="search" size={17} />
            </span>
            <input
              className="input input-with-icon"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search conversations"
              aria-label="Search conversations"
            />
          </div>
        )}

        {error && (
          <div style={{ marginTop: 16 }}>
            <Alert kind="error">{error}</Alert>
          </div>
        )}

        {loading && conversations.length === 0 ? (
          <LoadingBlock text="Loading your conversations…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="inbox"
            title={query ? 'No matches' : 'No conversations yet'}
            text={
              query
                ? `Nothing matches “${query}”.`
                : 'Once you start talking to someone, your chats will show up here.'
            }
            action={
              !query && (
                <button type="button" className="btn btn-gradient" onClick={onGoDiscover}>
                  <Icon name="flame" size={17} />
                  Find people
                </button>
              )
            }
          />
        ) : (
          <div className="conversation-list">
            {filtered.map(({ user, lastMessage }) => {
              const senderId = lastMessage?.sender?._id || lastMessage?.sender;
              const mine = String(senderId) === String(me.id);
              const body = lastMessage?.content || 'No messages yet';
              return (
                <button type="button" className="conversation-row" key={user._id} onClick={() => onOpenChat(user)}>
                  <Avatar user={user} size={52} showPresence showBotTag />
                  <div className="conversation-body">
                    <div className="conversation-head">
                      <span className="conversation-name">
                        {user.username}
                        {user.isBot && <BotBadge />}
                      </span>
                      <span className="conversation-time">{timeAgo(lastMessage?.timestamp)}</span>
                    </div>
                    <div className="conversation-preview">
                      {mine && <span style={{ color: 'var(--text-faint)' }}>You: </span>}
                      {body}
                    </div>
                  </div>
                  <Icon name="chevronRight" size={18} style={{ color: 'var(--text-faint)', flexShrink: 0 }} />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
